// 24×7 activity heatmap renderer (entity primitive). Takes the sparse
// `ContentHeatmapCell[]` from the API, backfills the dense grid via the
// pure helpers in lib/heatmap.ts, and paints each cell with an opacity
// scaled to the largest observed message count. Day labels follow ISO
// ordering (Mon..Sun) — same as the backend's `dayOfWeek` field.
//
// The component is render-only — no fetching, no Telegram-theme code.
// Color is the Tailwind `--primary` token so dark/light theme adapts via
// CSS custom properties; opacity carries the intensity so empty cells
// remain visible as a faint grid (dotted background).

import type { ContentHeatmapCell } from "@community-analytics/shared";

import { cn } from "@shared/lib";

import {
  HEATMAP_DAYS,
  HEATMAP_HOURS,
  denseHeatmap,
  heatmapIntensity,
  heatmapMaxMessages,
} from "../lib/heatmap";

export interface HeatmapGridProps {
  cells: ReadonlyArray<ContentHeatmapCell>;
  /** ISO day labels Mon..Sun, length 7. */
  dayLabels: readonly [string, string, string, string, string, string, string];
  /** Hour-row label rendered every 6 hours. */
  hourLabel: (hour: number) => string;
  /** Tooltip content per (day, hour) cell — formatted by the caller. */
  cellLabel: (params: {
    dayOfWeek: number;
    hour: number;
    messages: number;
  }) => string;
  emptyLabel: string;
  className?: string;
}

const HOUR_TICKS = [0, 6, 12, 18];

export function HeatmapGrid({
  cells,
  dayLabels,
  hourLabel,
  cellLabel,
  emptyLabel,
  className,
}: HeatmapGridProps) {
  const max = heatmapMaxMessages(cells);
  if (max === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed bg-background py-8 text-sm text-muted-foreground",
          className,
        )}
        role="status"
      >
        {emptyLabel}
      </div>
    );
  }
  const grid = denseHeatmap(cells);
  return (
    <div
      className={cn("flex flex-col gap-1", className)}
      role="img"
      aria-label={emptyLabel}
    >
      <div className="flex items-end gap-1 pl-10 text-[10px] text-muted-foreground">
        {Array.from({ length: HEATMAP_HOURS }, (_, h) => (
          <span
            key={h}
            className="flex-1 text-center tabular-nums"
            aria-hidden="true"
          >
            {HOUR_TICKS.includes(h) ? hourLabel(h) : ""}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-1">
        {Array.from({ length: HEATMAP_DAYS }, (_, day) => {
          const dayLabel = dayLabels[day] ?? "";
          return (
            <div key={day} className="flex items-center gap-1">
              <span
                className="w-9 flex-none text-[11px] text-muted-foreground"
                aria-hidden="true"
              >
                {dayLabel}
              </span>
              <div className="flex flex-1 gap-1">
                {Array.from({ length: HEATMAP_HOURS }, (_, hour) => {
                  const value = grid[day]?.[hour] ?? 0;
                  const intensity = heatmapIntensity(value, max);
                  const title = cellLabel({
                    dayOfWeek: day,
                    hour,
                    messages: value,
                  });
                  return (
                    <span
                      key={hour}
                      title={title}
                      aria-label={title}
                      className="aspect-square min-w-0 flex-1 rounded-sm border border-border/40 bg-primary"
                      style={{
                        // 0.04 keeps an empty cell faintly visible against
                        // dark mode; saturates to 0.95 so the most active
                        // slot doesn't disappear into the page background.
                        opacity: intensity === 0 ? 0.04 : 0.1 + intensity * 0.85,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
